import { fetchAccount, updateProfile } from './account-api';
import { confirmAction } from './ui';
import { t } from '../i18n';

/**
 * The stable code `POST /topup/checkout` answers (400) when the profile has no legal first/last
 * name — mirrors `PROFILE_NAME_REQUIRED` in `backend/src/game/payments/topup.service.ts`. The
 * payment gateway needs the real buyer; the backend never substitutes a placeholder.
 */
export const PROFILE_NAME_REQUIRED = 'PROFILE_NAME_REQUIRED';

/** True for the checkout refusal that asks for a name (the code rides in `error`, which core's
 * exception filter keeps; `code` is checked too in case it is ever passed through). */
export function isProfileNameRequired(err: unknown): boolean {
  if (!err || typeof err !== 'object' || !('status' in err)) return false;
  if ((err as { status: number }).status !== 400) return false;
  if ('code' in err && (err as { code: unknown }).code === PROFILE_NAME_REQUIRED) return true;
  const d = 'details' in err ? (err as { details: unknown }).details as { error?: unknown; code?: unknown } | undefined : undefined;
  return d?.error === PROFILE_NAME_REQUIRED || d?.code === PROFILE_NAME_REQUIRED;
}

const INPUT_CLASS =
  'min-h-[44px] w-full bg-surface-2 border border-border text-text text-sm px-3 touch-manipulation';

function field(label: string, input: HTMLInputElement): HTMLElement {
  const wrap = document.createElement('label');
  wrap.className = 'flex flex-col gap-1.5';
  const caption = document.createElement('span');
  caption.className = 'font-heading uppercase tracking-wide text-sm text-text';
  caption.textContent = label;
  wrap.append(caption, input);
  return wrap;
}

/**
 * Asks for the first/last name the payment provider needs, prefilled from the profile, and saves
 * it through `PATCH /account/profile` (the same fields as Settings → Identity). Resolves `true`
 * once both are saved — the caller then retries the checkout — and `false` when the player
 * cancels. Throws on a failed save, so the caller can show why.
 */
export async function promptProfileName(): Promise<boolean> {
  const first = document.createElement('input');
  const last = document.createElement('input');
  for (const [el, auto] of [[first, 'given-name'], [last, 'family-name']] as const) {
    el.type = 'text';
    el.className = INPUT_CLASS;
    el.maxLength = 60;
    el.setAttribute('autocomplete', auto);
  }
  first.dataset.shop = 'name-first';
  last.dataset.shop = 'name-last';
  try {
    const me = await fetchAccount();
    first.value = me.firstName ?? '';
    last.value = me.lastName ?? '';
  } catch {
    /* prefill is a convenience — an empty form still works */
  }

  const hint = document.createElement('p');
  hint.className = 'text-[12px] text-error hidden';

  for (;;) {
    const ok = await confirmAction({
      title: t('shop.nameRequired.title'),
      body: t('shop.nameRequired.body'),
      okLabel: t('shop.nameRequired.save'),
      extra: [field(t('settings.profile.firstName'), first), field(t('settings.profile.lastName'), last), hint],
    });
    if (!ok) return false;
    const firstName = first.value.trim();
    const lastName = last.value.trim();
    if (firstName && lastName) {
      await updateProfile({ firstName, lastName });
      return true;
    }
    hint.textContent = t('shop.nameRequired.missing');
    hint.classList.remove('hidden');
  }
}
